import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, AlertTriangle, Target, Shield } from 'lucide-react';

/**
 * Onboarding KPI Cards Component
 * 
 * Displays two horizontally-stacked demo KPI cards during onboarding:
 * - Tests Passed: Shows the testing/regression prevention value
 * - Incidents Caught: Shows the monitoring/prod drift detection value
 * 
 * Uses muted colors and demo data to illustrate future state without
 * competing with primary CTAs.
 */
const OnboardingKpiCards: React.FC = () => {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
      {/* Tests Passed Card */}
      <Card className="border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
        <CardContent className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-green-500" />
                <p className="text-sm font-medium text-muted-foreground">Tests Passed</p>
              </div>
              <p className="text-3xl font-bold text-gray-400">--</p>
              <p className="text-xs text-muted-foreground mt-1">
                Prevent regressions
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-500/60" />
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-white/80 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">After your first test:</span>
              <div className="mt-1 text-gray-600 dark:text-gray-400">
                Shows passing tests and trend analysis
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Incidents Caught Card */}
      <Card className="border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
        <CardContent className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-orange-500" />
                <p className="text-sm font-medium text-muted-foreground">Incidents Caught</p>
              </div>
              <p className="text-3xl font-bold text-gray-400">--</p>
              <p className="text-xs text-muted-foreground mt-1">
                Catch prod drift
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-orange-500/60" />
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-white/80 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">After monitoring setup:</span>
              <div className="mt-1 text-gray-600 dark:text-gray-400">
                Alerts when AI behavior changes
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingKpiCards;